import * as THREE from 'three';
import type { Random } from '../core/Random';
import { burnBarrel, deadTree } from './builders/grunge';
import { signBoard, tireStack } from './builders/props';
import { buildRide } from './builders/rides';
import type { WorldKit } from './districts';
import { GUN_RANGE, MUD_BOG, RODEO, SPOTS } from './layout';
import { buildRange, type Range } from './range';
import { box, cyl, M, mat, mesh, registerNightGlow, texMat, worldBox } from './materials';
import { signTexture, woodTexture } from './textures';

/**
 * The Tri-County Fairgrounds, east of the park: a rodeo arena with a real bull, the
 * mud bog, and Bubba's Gun Range out past the junkyard.
 */

export interface Bull {
  group: THREE.Group;
  head: THREE.Group;
  /** Somebody's riding: the idle animation leaves it alone. */
  busy?: boolean;
}

/** A blocky bull, facing +x. */
export function buildBull(color = '#3b2a20'): Bull {
  const g = new THREE.Group();
  const hide = mat(color, { rough: 0.9 });
  g.add(box(1.9, 0.95, 0.9, hide, 0, 1.15, 0));
  g.add(box(0.6, 0.5, 0.9, hide, 0.55, 1.75, 0));
  for (const x of [-0.7, 0.7]) for (const z of [-0.3, 0.3]) g.add(box(0.2, 0.75, 0.2, hide, x, 0.38, z));
  const tail = box(0.05, 0.7, 0.05, hide, -1.0, 1.1, 0);
  tail.rotation.z = -0.3;
  g.add(tail);
  const head = new THREE.Group();
  head.position.set(1.0, 1.35, 0);
  head.add(box(0.6, 0.5, 0.55, hide, 0.25, 0, 0));
  head.add(box(0.2, 0.25, 0.45, mat('#5a4030'), 0.6, -0.12, 0));
  head.add(cyl(0.05, 0.08, 0.35, mat('#e9dcc0'), 0.15, 0.25, 0.32, 6).rotateX(-0.9));
  head.add(cyl(0.05, 0.08, 0.35, mat('#e9dcc0'), 0.15, 0.25, -0.32, 6).rotateX(0.9));
  head.add(mesh(new THREE.TorusGeometry(0.06, 0.015, 6, 10), M.chrome, 0.72, -0.15, 0));
  g.add(head);
  return { group: g, head };
}

function fencePanel(length: number, height: number, wood: THREE.Material): THREE.Group {
  const g = new THREE.Group();
  for (const y of [0.4, 0.85, 1.3].filter((v) => v < height)) g.add(box(length, 0.14, 0.06, wood, 0, y, 0));
  return g;
}

function bleachers(width: number, rows: number): THREE.Group {
  const g = new THREE.Group();
  const plank = mat('#b8b3a6', { metal: 0.4, rough: 0.5 });
  for (let i = 0; i < rows; i++) {
    g.add(box(width, 0.06, 0.32, plank, 0, 0.45 + i * 0.42, -i * 0.62));
    g.add(box(width, 0.06, 0.25, plank, 0, 0.22 + i * 0.42, -i * 0.62 + 0.3));
  }
  for (const x of [-width / 2 + 0.2, 0, width / 2 - 0.2]) {
    const leg = box(0.1, rows * 0.42 + 0.4, 0.1, M.steel, x, (rows * 0.42 + 0.4) / 2, -(rows - 1) * 0.62);
    g.add(leg);
    const brace = box(0.08, 0.08, rows * 0.7, M.steel, x, rows * 0.2, -(rows - 1) * 0.31);
    brace.rotation.x = 0.55;
    g.add(brace);
  }
  return g;
}

function floodlight(): { group: THREE.Group; lamp: THREE.MeshStandardMaterial } {
  const g = new THREE.Group();
  g.add(cyl(0.12, 0.16, 9, M.steel, 0, 4.5, 0, 8));
  g.add(box(1.4, 0.7, 0.2, M.black, 0, 9.1, 0));
  const lamp = new THREE.MeshStandardMaterial({
    color: '#f7f2d0',
    emissive: new THREE.Color('#fff2c0'),
    emissiveIntensity: 0,
  });
  registerNightGlow(lamp, 2.2);
  for (const x of [-0.4, 0.4])
    for (const y of [8.95, 9.25]) g.add(box(0.3, 0.22, 0.04, lamp, x, y, 0.12, { cast: false }));
  return { group: g, lamp };
}

export interface Fairgrounds {
  bull: Bull;
  range: Range;
}

export function buildFairgrounds(kit: WorldKit, rng: Random): Fairgrounds {
  const wood = texMat(woodTexture(), { color: '#a07850' });

  // --- the entrance sign on the highway -------------------------------------------
  const s = SPOTS.fairgroundsSign;
  const sign = signBoard(
    {
      w: 1024,
      h: 448,
      bg: '#f4e9cf',
      fg: '#1d3b6e',
      border: '#7a1f17',
      borderWidth: 18,
      gap: 8,
      lines: [
        { text: 'TRI-COUNTY FAIRGROUNDS', size: 92, font: 'Rye' },
        { text: 'RODEO • MUD BOG • GUN RANGE', size: 64, font: 'Anton', color: '#7a1f17' },
        { text: 'TURN HERE • NO REFUNDS • NO WHINING', size: 40, font: 'Anton', color: '#333' },
      ],
    },
    4.6,
    2.0,
    1.6,
  );
  kit.place(sign, s.x, s.z);
  kit.collision.addRect(s.x, s.z - 0.05, 4.6, 0.4);

  // --- rodeo arena -------------------------------------------------------------------
  const { x: rx, z: rz, rx: ax, rz: az } = RODEO;
  const posts = 40;
  for (let i = 0; i < posts; i++) {
    const a0 = (i / posts) * Math.PI * 2;
    const a1 = ((i + 1) / posts) * Math.PI * 2;
    const p0 = { x: rx + Math.cos(a0) * ax, z: rz + Math.sin(a0) * az };
    const p1 = { x: rx + Math.cos(a1) * ax, z: rz + Math.sin(a1) * az };
    kit.root.add(box(0.18, 1.7, 0.18, M.trunk, p0.x, 0.85, p0.z));
    // Leave a gap on the west side for the chute gate.
    const gate = Math.abs(Math.cos(a0) + 1) < 0.02 || Math.abs(Math.cos(a1) + 1) < 0.02;
    if (gate) continue;
    const len = Math.hypot(p1.x - p0.x, p1.z - p0.z);
    const panel = fencePanel(len, 1.6, wood);
    panel.position.set((p0.x + p1.x) / 2, 0, (p0.z + p1.z) / 2);
    panel.rotation.y = -Math.atan2(p1.z - p0.z, p1.x - p0.x);
    kit.root.add(panel);
    kit.collision.addCircle(p0.x, p0.z, 0.6);
    kit.collision.addCircle((p0.x + p1.x) / 2, (p0.z + p1.z) / 2, 0.6);
  }
  // Bucking chute and the pen next to it, west side.
  const c = SPOTS.rodeoChute;
  const chute = new THREE.Group();
  for (const z of [-1.2, 1.2]) chute.add(fencePanel(3.2, 1.9, wood).translateZ(z));
  for (const x of [-1.6, 1.6]) for (const z of [-1.2, 1.2]) chute.add(box(0.2, 2.2, 0.2, M.trunk, x, 1.1, z));
  chute.add(box(0.1, 1.9, 2.4, mat('#7a1f17'), 1.6, 1.0, 0));
  kit.place(chute, c.x - 0.5, c.z + 2.6);
  kit.collision.addRect(c.x - 0.5, c.z + 3.8, 3.4, 0.3);
  kit.collision.addRect(c.x - 0.5, c.z + 1.4, 3.4, 0.3);
  const bull = buildBull();
  kit.place(bull.group, c.x - 0.6, c.z + 2.6, 0, kit.dynamic);
  bull.group.traverse((o) => (o.userData.noBatch = true));
  // Bleachers on the south side, an announcer's booth and floodlights.
  const b = SPOTS.bleachers;
  for (const dx of [-7, 7]) {
    const stand = bleachers(10, 5);
    kit.place(stand, b.x + dx, b.z, Math.PI);
    kit.collision.addRect(b.x + dx, b.z + 1.3, 10, 3.4);
  }
  const booth = new THREE.Group();
  booth.add(box(2.6, 2.2, 2.2, wood, 0, 3.3, 0));
  booth.add(box(2.8, 0.15, 2.4, mat('#5a5652'), 0, 4.48, 0));
  for (const x of [-1.1, 1.1])
    for (const z of [-0.9, 0.9]) booth.add(box(0.15, 2.2, 0.15, M.trunk, x, 1.1, z));
  booth.add(
    mesh(new THREE.PlaneGeometry(2.2, 0.8), M.darkGlass, 0, 3.6, -1.11, { cast: false }).rotateY(Math.PI),
  );
  const boothSign = signTexture({
    w: 512,
    h: 128,
    bg: '#7a1f17',
    fg: '#f4e9cf',
    lines: [{ text: 'RODEO TONIGHT', size: 80, font: 'Rye' }],
  });
  booth.add(
    mesh(new THREE.PlaneGeometry(2.4, 0.6), texMat(boothSign), 0, 2.0, -1.11, { cast: false }).rotateY(
      Math.PI,
    ),
  );
  kit.place(booth, b.x, b.z + 0.6);
  for (const x of [-1.1, 1.1]) kit.collision.addCircle(b.x + x, b.z + 0.6, 0.2);
  for (const [dx, dz] of [
    [-ax - 2, -az - 1],
    [ax + 2, -az - 1],
    [-ax - 2, az + 1],
    [ax + 2, az + 1],
  ] as const) {
    const f = floodlight();
    kit.place(f.group, rx + dx, rz + dz, Math.atan2(-dx, -dz));
    kit.collision.addCircle(rx + dx, rz + dz, 0.25);
  }
  // Hay bales and barrels around the ring.
  for (let i = 0; i < 10; i++) {
    const a = rng.range(0, Math.PI * 2);
    const hx = rx + Math.cos(a) * (ax + 3.5);
    const hz = rz + Math.sin(a) * (az + 3.5);
    if (Math.hypot(hx - b.x, hz - b.z) < 9 || hx < rx - ax) continue;
    kit.place(box(1.1, 0.55, 0.6, mat('#d9b45a', { rough: 1 })), hx, hz, rng.range(0, 3)).position.y = 0.28;
    kit.collision.addCircle(hx, hz, 0.6);
  }
  for (const [dx, dz] of [
    [0, -5],
    [6, 4],
    [-5, 3],
  ] as const) {
    kit.root.add(cyl(0.35, 0.35, 0.9, mat('#c62828'), rx + dx, 0.45, rz + dz, 10));
    kit.collision.addCircle(rx + dx, rz + dz, 0.4);
  }

  // --- the mud bog -------------------------------------------------------------------
  const M_ = MUD_BOG;
  const mudCx = (M_.minX + M_.maxX) / 2;
  const mudCz = (M_.minZ + M_.maxZ) / 2;
  const mudW = M_.maxX - M_.minX;
  const mudD = M_.maxZ - M_.minZ;
  const wet = new THREE.MeshStandardMaterial({ color: '#4a3826', roughness: 0.35, metalness: 0.05 });
  const mud = mesh(new THREE.PlaneGeometry(mudW - 2, mudD - 2, 24, 12), wet, mudCx, 0.07, mudCz, {
    cast: false,
  });
  mud.rotation.x = -Math.PI / 2;
  // Lumps and ruts, always above the ground so it never shows through.
  const pos = mud.geometry.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++)
    pos.setZ(i, rng.next() * 0.05 + (Math.sin(pos.getX(i) * 0.8) * 0.5 + 0.5) * 0.04);
  mud.geometry.computeVertexNormals();
  kit.root.add(mud);
  // Log walls along the long sides, open at both ends.
  for (const z of [M_.minZ, M_.maxZ]) {
    for (let x = M_.minX + 4; x < M_.maxX - 4; x += 3.2) {
      const log = cyl(0.28, 0.28, 3.1, mat('#6b4a2b', { rough: 1 }), x, 0.28, z, 8);
      log.rotation.z = Math.PI / 2;
      kit.root.add(log);
    }
    kit.collision.addRect(mudCx, z, mudW - 8, 0.6);
  }
  // Start and finish arches.
  const arch = (x: number, text: string, color: string): void => {
    const g = new THREE.Group();
    for (const dz of [-mudD / 2 + 1, mudD / 2 - 1]) g.add(box(0.3, 4.4, 0.3, M.trunk, 0, 2.2, dz));
    g.add(box(0.3, 0.3, mudD - 1.6, M.trunk, 0, 4.4, 0));
    const tex = signTexture({
      w: 1024,
      h: 160,
      bg: color,
      fg: '#ffffff',
      lines: [{ text, size: 110, font: 'Bangers' }],
    });
    for (const s of [-1, 1]) {
      const m = mesh(new THREE.PlaneGeometry(mudD - 3, 0.9), texMat(tex), s * 0.17, 3.85, 0, { cast: false });
      m.rotation.y = (s * Math.PI) / 2;
      g.add(m);
    }
    kit.place(g, x, mudCz);
    for (const dz of [-mudD / 2 + 1, mudD / 2 - 1]) kit.collision.addCircle(x, mudCz + dz, 0.25);
  };
  arch(M_.minX + 1.5, 'MUD RUN START', '#2f7d32');
  arch(M_.maxX - 1.5, 'TURN AROUND, HOSS', '#c62828');
  const ms = SPOTS.mudSign;
  const mudSign = signBoard(
    {
      w: 768,
      h: 384,
      bg: '#3d2a1a',
      fg: '#f4e9cf',
      border: '#f4e9cf',
      borderWidth: 10,
      gap: 6,
      lines: [
        { text: 'MUD BOG', size: 120, font: 'Bangers', color: '#ffd400' },
        { text: '$10 A RUN • THERE AND BACK • FASTEST WINS', size: 40, font: 'Anton' },
        { text: 'IF YOU GET STUCK, YOU GET STUCK', size: 36, font: 'Anton', color: '#d9b45a' },
      ],
      grime: true,
    },
    2.8,
    1.4,
    1.0,
  );
  kit.place(mudSign, ms.x, ms.z, -Math.PI / 4);
  kit.collision.addCircle(ms.x, ms.z, 0.6);
  // A truck that didn't make it, axle-deep in the corner.
  const stuck = buildRide('elcamino');
  stuck.group.traverse((o) => (o.userData.noBatch = false));
  stuck.group.position.y = -0.35;
  stuck.group.rotation.z = 0.12;
  kit.place(stuck.group, M_.maxX - 9, M_.minZ + 3.2, 0.4);
  kit.collision.addRect(M_.maxX - 9, M_.minZ + 3.2, 5.2, 2.6);
  kit.place(tireStack(rng, 4), M_.minX - 3, M_.minZ - 2);
  kit.collision.addCircle(M_.minX - 3, M_.minZ - 2, 0.5);

  // --- Bubba's Gun Range ----------------------------------------------------------------
  const G = GUN_RANGE;
  const gx = (G.minX + G.maxX) / 2;
  // Dirt berm across the far end.
  const bermShape = new THREE.Shape();
  bermShape.moveTo(-3, 0);
  bermShape.lineTo(-0.8, 3.2);
  bermShape.lineTo(0.8, 3.2);
  bermShape.lineTo(3, 0);
  bermShape.closePath();
  const bermGeo = new THREE.ExtrudeGeometry(bermShape, { depth: G.maxX - G.minX, bevelEnabled: false });
  bermGeo.translate(0, 0, -(G.maxX - G.minX) / 2);
  const berm = mesh(bermGeo, mat('#7a6142', { flat: true, rough: 1 }), gx, 0, G.minZ + 3);
  berm.rotation.y = Math.PI / 2;
  kit.root.add(berm);
  kit.collision.addRect(gx, G.minZ + 3, G.maxX - G.minX, 6);
  // A fence rail for cans (the cans, the pop-up steel and the clays live in range.ts),
  // and paper targets on frames.
  const rail = new THREE.Group();
  rail.add(box(20, 0.12, 0.25, wood, 0, 1.0, 0));
  for (let x = -9.5; x <= 9.5; x += 2.4) rail.add(box(0.15, 1.0, 0.15, M.trunk, x, 0.5, 0));
  kit.place(rail, gx, G.minZ + 12);
  const range = buildRange(kit.dynamic);
  const targetTex = signTexture({
    w: 256,
    h: 256,
    bg: '#f4f1e8',
    fg: '#c62828',
    lines: [{ text: '◎', size: 230, font: 'Anton' }],
  });
  for (const x of [-12, -3.5, 12]) {
    const frame = new THREE.Group();
    for (const s of [-0.65, 0.65]) frame.add(box(0.1, 2.2, 0.1, M.trunk, s, 1.1, 0));
    frame.add(mesh(new THREE.PlaneGeometry(1.2, 1.2), texMat(targetTex), 0, 1.5, 0.06, { cast: false }));
    kit.place(frame, gx + x, G.minZ + 6.2);
  }
  // Covered firing line at the south end, with lanes and a counter.
  const shed = new THREE.Group();
  shed.add(mesh(worldBox(22, 0.12, 4, 1), texMat(woodTexture()), 0, 0.06, 0));
  for (const x of [-10.8, -5.4, 0, 5.4, 10.8])
    for (const z of [-1.8, 1.8]) shed.add(box(0.18, 3, 0.18, M.trunk, x, 1.5, z));
  const roof = box(23, 0.1, 5, mat('#8d8f8a', { metal: 0.5, rough: 0.5 }), 0, 3.05, 0.2);
  roof.rotation.x = -0.08;
  shed.add(roof);
  for (let i = 0; i < 5; i++) shed.add(box(0.06, 1.2, 1.6, wood, -8.6 + i * 4.3, 0.6, -0.8));
  for (let i = 0; i < 4; i++) shed.add(box(1.6, 0.08, 0.6, wood, -6.45 + i * 4.3, 1.0, -1.5));
  kit.place(shed, gx, G.maxZ - 4.5);
  for (const x of [-10.8, -5.4, 0, 5.4, 10.8])
    for (const z of [-1.8, 1.8]) kit.collision.addCircle(gx + x, G.maxZ - 4.5 + z, 0.18);
  const counter = SPOTS.gunCounter;
  kit.root.add(box(2.4, 1.0, 0.7, wood, counter.x + 3, 0.5, counter.z - 0.6));
  kit.collision.addRect(counter.x + 3, counter.z - 0.6, 2.4, 0.7);
  const rangeSign = signBoard(
    {
      w: 768,
      h: 384,
      bg: '#1f3b24',
      fg: '#f4e9cf',
      border: '#f4e9cf',
      borderWidth: 10,
      gap: 6,
      lines: [
        { text: "BUBBA'S GUN RANGE", size: 84, font: 'Rye', color: '#ffd400' },
        { text: 'CANS & PAPER ONLY • EYES & EARS', size: 42, font: 'Anton' },
        { text: 'ALL GUNS POINTED AT THE BERM. ALWAYS.', size: 36, font: 'Anton', color: '#ff8a8a' },
      ],
    },
    3.0,
    1.5,
    1.0,
  );
  kit.place(rangeSign, G.minX - 2.5, G.maxZ + 1.5, Math.PI / 2);
  kit.collision.addCircle(G.minX - 2.5, G.maxZ + 1.5, 0.4);
  // Shell casings and a burn barrel; a dead tree for atmosphere.
  const bb = burnBarrel();
  kit.place(bb.group, G.maxX + 2, G.maxZ - 2);
  kit.collision.addCircle(G.maxX + 2, G.maxZ - 2, 0.4);
  kit.place(deadTree(rng), G.minX - 3, G.minZ + 10);

  return { bull, range };
}
